import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { debounce, isEmpty } from 'lodash';

import { faCheckCircle, faPeopleRoof, faPlus, faSearch } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuildoutLogo } from 'helpers/custom_fontawesome_icons';

import { Button, Form, ListGroup, OverlayTrigger, Popover } from 'react-bootstrap-5';

import { addContactToProperty, fetchCompanies, fetchContacts } from 'helpers/clarity_api';

export default function PropertyContacts({ id }) {
  const [showOverlay, setShowOverlay] = useState(false);
  const [showSearchTool, setShowSearchTool] = useState(true);
  const [added, setAdded] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const iframeRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (showOverlay) {
      handleChange();
      setAdded(false);
    }
  }, [showOverlay]);

  useEffect(() => {
    if (added) {
      setTimeout(() => handleCloseOverlay(), 3000);
    }
  }, [added]);

  const handleMessage = (event) => {
    const { eventName, payload } = event?.data || {};

    if (eventName === 'pn-contact-submit' && payload?.success) {
      iframeRef.current.src = `${window.CLARITY_URL}/research/${id}/request_ownerships`;

      setShowSearchTool(true);
    }
  };

  useEffect(() => {
    if (iframeRef.current) {
      window.addEventListener('message', handleMessage);
    }

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const debouncedSearch = debounce((searchString) => {
    fetchContacts(searchString).then(setContacts);
    fetchCompanies(searchString).then(setCompanies);
  }, 300);

  const handleCloseOverlay = () => {
    if (inputRef.current) inputRef.current.value = null;

    setShowOverlay(false);
  };

  const handleChange = () => debouncedSearch(inputRef?.current?.value || '');

  const handleAddContact = (contactId, contactType) => {
    addContactToProperty(contactId, contactType, id).then(() => {
      setAdded(true);

      iframeRef.current.src = `${window.CLARITY_URL}/research/${id}/request_ownerships`;
    });
  };

  const handleNewContact = () => {
    handleCloseOverlay();
    setShowSearchTool(false);

    iframeRef.current.src =
      `${window.CLARITY_URL}/research/${id}/new_contact?owned_type=Insights::Property&owned_id=${id}`;
  };

  const popover = (
    <Popover style={{ maxWidth: '500px', minWidth: '320px' }}>
      {added && (
        <Popover.Body>
          <div className="text-primary animate__animated animate__fadeIn" style={{ fontSize: '1rem' }}>
            <FontAwesomeIcon className="me-2" icon={faCheckCircle} />
            Successfully added!
          </div>
        </Popover.Body>
      )}
      {!added && (
        <Popover.Body>
          <div className="with-icon my-2">
            <div className="icon search-icon">
              <FontAwesomeIcon icon={faSearch}/>
            </div>

            <Form.Control ref={inputRef} type="text" onChange={handleChange}/>
          </div>

          <div className="mb-2" style={{ maxHeight: '200px', overflow: 'auto' }}>
            {!isEmpty(contacts) && (
              <ListGroup>
                <ListGroup.Item className="border-0 fw-bold">Contacts</ListGroup.Item>
                {
                  contacts.map(contact => (
                    <ListGroup.Item
                      action
                      className="border-0"
                      key={contact.id}
                      onClick={() => handleAddContact(contact.id, 'Contact', contact.display_name)}
                    >
                      {contact.display_name}
                    </ListGroup.Item>
                  ))
                }
              </ListGroup>
            )}

            {!isEmpty(companies) && (
              <ListGroup>
                <ListGroup.Item className="border-0 fw-bold">Companies</ListGroup.Item>
                {
                  companies.map(company => (
                    <ListGroup.Item
                      action
                      className="border-0"
                      key={company.id}
                      onClick={() => handleAddContact(company.id, 'Company', company.display_name)}
                    >
                      {company.display_name}
                    </ListGroup.Item>
                  ))
                }
              </ListGroup>
            )}

            {isEmpty(contacts) && isEmpty(companies) && (
              <div className="py-2 px-3">No results found</div>
            )}
          </div>

          <Button className="w-100" variant="outline-primary" onClick={handleNewContact}>
            <FontAwesomeIcon className="me-2" icon={faPlus} size="sm" />
            New Contact
          </Button>
        </Popover.Body>
      )}
    </Popover>
  );

  return (
    <React.Fragment>
      {
        showSearchTool && (
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div className="d-flex align-items-center gap-2">
              <FontAwesomeIcon className="text-secondary" icon={faPeopleRoof} />
              <h6 className="my-0 fw-bolder">Contacts</h6>
            </div>
            <OverlayTrigger
              overlay={popover}
              placement="bottom"
              rootClose
              show={showOverlay}
              trigger="click"
              onToggle={show => setShowOverlay(show)}
            >
              <Button variant="primary">Add Contact</Button>
            </OverlayTrigger>
          </div>
        )
      }
      <div className="position-relative" data-controller="pnSettings">
        {loading && (
          <div
            className="d-flex align-items-center justify-content-center position-absolute w-100 h-100"
            style={{ minHeight: '200px' }}
          >
            <FontAwesomeIcon beatFade className="text-primary" icon={faBuildoutLogo} size="3x" />
          </div>
        )}
        <iframe
          className={`w-100 ${loading ? 'opacity-50' : ''}`}
          data-pnSettings-target="iframe"
          loading="lazy"
          ref={iframeRef}
          src={`${window.CLARITY_URL}/research/${id}/request_ownerships`}
          onLoad={() => setLoading(false)}
        />
      </div>
    </React.Fragment>
  );
}

PropertyContacts.propTypes = {
  id: PropTypes.number.isRequired
};
